import { useState } from 'react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import {
  FlaskConical, CheckCircle2, Loader2, AlertTriangle, MessageSquare,
  Scale, ChevronDown, ChevronUp, Shield,
} from 'lucide-react';
import { DebatePanelShowcase } from './DebatePanelShowcase';
import { MediatorShowcase } from './MediatorShowcase';
import type {
  StrategicPhase, StressTestResult, DebateArgument, MediatorConsensus,
} from '@/hooks/useStrategicStream';

// ============================================================================
// STRESS TEST SYNTHESIS SUMMARY
// ============================================================================

function StressTestSynthesis({ results, isSmallScreen }: { results: StressTestResult[]; isSmallScreen: boolean }) {
  if (results.length === 0) return null;

  const validatedCount = results.filter(r => r.testResult === 'validated').length;
  const invalidatedCount = results.filter(r => r.testResult === 'invalidated').length;
  const avgConfidence = results.reduce((sum, r) => sum + r.confidence, 0) / results.length;

  const overallHealth = validatedCount > invalidatedCount * 2 ? 'strong'
    : invalidatedCount > validatedCount ? 'weak' : 'mixed';

  const healthConfig = {
    strong: { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', label: 'STRONG' },
    mixed: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'MIXED' },
    weak: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'WEAK' },
  }[overallHealth];

  return (
    <div className={cn('rounded-lg p-3 mb-3 flex items-center gap-3', healthConfig.bg, 'border', healthConfig.border)}>
      <Shield className={cn('w-5 h-5 flex-shrink-0', healthConfig.color)} />
      <div className="flex-1 min-w-0">
        <div className={cn('font-semibold flex items-center gap-2', healthConfig.color, isSmallScreen ? 'text-sm' : 'text-base')}>
          Foundation Strength: {healthConfig.label}
        </div>
        <div className={cn('text-text-secondary', isSmallScreen ? 'text-xs' : 'text-sm')}>
          {validatedCount}/{results.length} validated, avg {Math.round(avgConfidence * 100)}% confidence
          {invalidatedCount > 0 && ` — ${invalidatedCount} assumption${invalidatedCount > 1 ? 's' : ''} challenged`}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INLINE STRESS TEST RESULTS
// ============================================================================

function StressTestResultsInline({ results, isSmallScreen }: { results: StressTestResult[]; isSmallScreen: boolean }) {
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  const toggleResult = (index: number) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {results.map((r, i) => {
        const isValidated = r.testResult === 'validated';
        const isInvalidated = r.testResult === 'invalidated';
        const isExpanded = expandedResults.has(i);
        const hasEvidence = (r.evidenceFor?.length ?? 0) > 0 || (r.evidenceAgainst?.length ?? 0) > 0;

        return (
          <div key={i} className={cn('rounded-lg overflow-hidden', isSmallScreen ? 'text-xs' : 'text-sm',
            isValidated ? 'bg-green-500/10 border border-green-500/20' : isInvalidated ? 'bg-red-500/10 border border-red-500/20' : 'bg-yellow-500/10 border border-yellow-500/20')}>
            <button
              onClick={() => hasEvidence && toggleResult(i)}
              aria-expanded={isExpanded}
              className={cn('w-full flex items-center gap-2 px-4 py-3 text-left', hasEvidence && 'cursor-pointer hover:bg-surface-light')}
            >
              {isValidated ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" /> :
                isInvalidated ? <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" /> :
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />}
              <span className={cn('flex-1', isValidated ? 'text-green-300' : isInvalidated ? 'text-red-300' : 'text-yellow-300')}>
                {r.assumptionStatement || `Hypothesis ${i + 1}`}
              </span>
              {r.confidence !== undefined && (
                <span className={cn('px-2 py-0.5 rounded text-xs font-mono',
                  r.confidence >= 0.7 ? 'bg-green-500/20 text-green-400' :
                  r.confidence >= 0.4 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400')}>
                  {(r.confidence * 100).toFixed(0)}%
                </span>
              )}
              {hasEvidence && (isExpanded ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />)}
            </button>
            {isExpanded && hasEvidence && (
              <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-3" role="region">
                {r.evidenceFor && r.evidenceFor.length > 0 && (
                  <div>
                    <div className="text-green-400/80 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Evidence Supporting
                    </div>
                    <ul className="list-disc list-inside text-text-primary space-y-1 pl-1">
                      {r.evidenceFor.map((e, j) => (
                        <li key={j} className="text-xs leading-relaxed">{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {r.evidenceAgainst && r.evidenceAgainst.length > 0 && (
                  <div>
                    <div className="text-red-400/80 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Evidence Against
                    </div>
                    <ul className="list-disc list-inside text-text-primary space-y-1 pl-1">
                      {r.evidenceAgainst.map((e, j) => (
                        <li key={j} className="text-xs leading-relaxed">{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// MAIN CONTAINER
// ============================================================================

export function HypothesisStressTestContainer({
  phase,
  stressTestResults,
  debateArguments,
  mediatorConsensus,
  isSmallScreen
}: {
  phase: StrategicPhase;
  stressTestResults: StressTestResult[];
  debateArguments: DebateArgument[];
  mediatorConsensus?: MediatorConsensus;
  isSmallScreen: boolean;
}) {
  const isStressTestActive = phase === 'stress_test';
  const isDebateActive = phase === 'debate';
  const isMediationActive = phase === 'mediation';
  const isContainerActive = isStressTestActive || isDebateActive || isMediationActive;
  const isContainerComplete = ['aggregation', 'concluded'].includes(phase);

  const showStressTest = ['stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase);
  const showDebate = ['debate', 'mediation', 'aggregation', 'concluded'].includes(phase);
  const showMediator = ['mediation', 'aggregation', 'concluded'].includes(phase);

  return (
    <div className={cn(
      'mx-auto rounded-2xl border-2 backdrop-blur transition-all duration-300',
      isContainerActive && 'border-purple-500/60 shadow-lg shadow-purple-500/20',
      isContainerComplete ? 'border-purple-500/40 bg-gradient-to-br from-purple-500/5 to-purple-900/10' : 'border-purple-500/20 bg-purple-500/5',
      isSmallScreen ? 'max-w-[340px] p-3' : 'max-w-[900px] p-6'
    )}>
      <div className={cn('flex items-center justify-center gap-2 mb-4 text-purple-400 font-semibold uppercase', isSmallScreen ? 'text-sm' : 'text-lg')}>
        {isContainerComplete ? <CheckCircle2 className="w-5 h-5" /> : isContainerActive ? <Loader2 className="w-5 h-5 animate-spin" /> : <FlaskConical className="w-5 h-5" />}
        <span>Hypothesis Stress Test <InfoTooltip content="Key findings are tested against challenging counter-scenarios. Surviving stress tests means the recommendation is more robust." position="right" /></span>
      </div>
      <div className={cn('text-text-secondary text-center mb-6', isSmallScreen ? 'text-sm' : 'text-base')}>
        Validate strategic assumptions through rigorous testing and AI council debate
      </div>

      {showStressTest && stressTestResults.length > 0 && (
        <div className="mb-6">
          <div className={cn('text-purple-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
            <FlaskConical className="w-4 h-4" />
            Assumption Testing Results
          </div>
          <StressTestSynthesis results={stressTestResults} isSmallScreen={isSmallScreen} />
          <StressTestResultsInline results={stressTestResults} isSmallScreen={isSmallScreen} />
        </div>
      )}

      {showDebate && (
        <div className={cn(
          'rounded-xl border backdrop-blur transition-all duration-300 mb-6',
          isDebateActive && 'border-red-500/60 shadow-md shadow-red-500/10',
          ['mediation', 'aggregation', 'concluded'].includes(phase) ? 'border-red-500/30 bg-red-500/5' : 'border-red-500/20 bg-red-500/5',
          isSmallScreen ? 'p-4' : 'p-5'
        )}>
          <div className={cn('flex items-center gap-2 mb-3 text-red-400 font-semibold uppercase', isSmallScreen ? 'text-sm' : 'text-base')}>
            {['mediation', 'aggregation', 'concluded'].includes(phase) ? <CheckCircle2 className="w-5 h-5" /> : isDebateActive ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageSquare className="w-5 h-5" />}
            <span>AI Council Debate</span>
            {debateArguments.length > 0 && <span className="text-text-secondary font-normal">({debateArguments.length} arguments)</span>}
          </div>
          <div className={cn('text-text-secondary mb-4', isSmallScreen ? 'text-xs' : 'text-sm')}>Strategic debaters challenge assumptions</div>
          <DebatePanelShowcase debateArgs={debateArguments} isActive={isDebateActive} isSmallScreen={isSmallScreen} />
        </div>
      )}

      {showMediator && (
        <div className={cn(
          'rounded-xl border backdrop-blur transition-all duration-300',
          isMediationActive && 'border-cyan-500/60 shadow-md shadow-cyan-500/10',
          ['aggregation', 'concluded'].includes(phase) ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-cyan-500/20 bg-cyan-500/5',
          isSmallScreen ? 'p-4' : 'p-5'
        )}>
          <div className={cn('flex items-center gap-2 mb-3 text-cyan-400 font-semibold uppercase', isSmallScreen ? 'text-sm' : 'text-base')}>
            {['aggregation', 'concluded'].includes(phase) ? <CheckCircle2 className="w-5 h-5" /> : isMediationActive ? <Loader2 className="w-5 h-5 animate-spin" /> : <Scale className="w-5 h-5" />}
            <span>Mediator</span>
          </div>
          <div className={cn('text-text-secondary mb-4', isSmallScreen ? 'text-xs' : 'text-sm')}>Build consensus from debate</div>
          <MediatorShowcase consensus={mediatorConsensus} isActive={isMediationActive} isSmallScreen={isSmallScreen} />
        </div>
      )}
    </div>
  );
}
